import React, { useState, useEffect } from 'react';
import { Spinner, Badge } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { dailyRecordsAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';
import AuthImage, { openAuthFile } from '../../components/Common/AuthImage';

interface DailyRecordFull {
  id: string;
  recordDate: string;
  isCompleted: boolean;
  weatherCondition: string | null;
  temperature: number | null;
  notes: string | null;
  createdAt: string;
  site: {
    name: string;
    project: {
      name: string;
      city: { name: string };
    };
  };
  createdBy: { id: string; firstName: string; lastName: string };
  personnelEntries: Array<{
    id: string;
    dailyWage: number;
    overtimeHours: number | null;
    personnel: { id: string; firstName: string; lastName: string; title: string | null };
  }>;
  vehicleEntries: Array<{
    id: string;
    usageType: string;
    notes: string | null;
    km: number | null;
    hours: number | null;
    driverId: string | null;
    driverType: string | null;
    vehicle: { id: string; plate: string; brand: string | null; model: string | null; isMachinery?: boolean };
  }>;
  workEntries: Array<{
    id: string;
    quantity: number;
    notes: string | null;
    siteWork: {
      id: string;
      workType: { name: string; unit: string };
    };
  }>;
  photos: Array<{
    id: string;
    fileName: string;
    filePath: string;
    description: string | null;
  }>;
}

interface DailyRecordDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  recordId: string | null;
  onUpdate?: () => void;
}

const weatherLabels: Record<string, string> = {
  SUNNY: 'Güneşli',
  CLOUDY: 'Bulutlu',
  RAINY: 'Yağmurlu',
  SNOWY: 'Karlı',
  STORMY: 'Fırtınalı',
};

const weatherIcons: Record<string, string> = {
  SUNNY: 'ri-sun-line',
  CLOUDY: 'ri-cloudy-line',
  RAINY: 'ri-rainy-line',
  SNOWY: 'ri-snowy-line',
  STORMY: 'ri-thunderstorms-line',
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    weekday: 'long',
  });
};

const getUsageTypeLabel = (type: string) => {
  switch (type) {
    case 'FULL_DAY':
      return 'Tam Gün';
    case 'HALF_DAY':
      return 'Yarım Gün';
    default:
      return type;
  }
};

const DailyRecordDetailModal: React.FC<DailyRecordDetailModalProps> = ({
  isOpen,
  toggle,
  recordId,
  onUpdate,
}) => {
  const isAdmin = useIsAdmin();
  const [isExpanded, setIsExpanded] = useState(false);
  const [record, setRecord] = useState<DailyRecordFull | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && recordId) {
      fetchRecord();
    }
  }, [isOpen, recordId]);

  const fetchRecord = async () => {
    if (!recordId) return;

    try {
      setLoading(true);
      const response = await dailyRecordsAPI.getOne(recordId);
      setRecord(response.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Kayıt detayları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getDriverName = (entry: DailyRecordFull['vehicleEntries'][0]) => {
    if (!entry.driverId) return '-';
    if (entry.driverType === 'USER') {
      return `${record?.createdBy.firstName} ${record?.createdBy.lastName} (Formen)`;
    }
    const personnel = record?.personnelEntries.find(
      (pe) => pe.personnel.id === entry.driverId
    );
    if (personnel) {
      return `${personnel.personnel.firstName} ${personnel.personnel.lastName}`;
    }
    return '-';
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const handleDelete = async () => {
    if (!record) return;
    if (!window.confirm('Bu günlük kaydı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) return;
    try {
      await dailyRecordsAPI.delete(record.id);
      toast.success('Günlük kayıt silindi');
      toggle();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  const headerActions = (
    <>
      {isAdmin && record && (
        <button
          type="button"
          className="side-modal-expand-btn me-1"
          onClick={handleDelete}
          title="Sil"
          style={{ color: '#f06548' }}
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      )}
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Günlük Kayıt Detayı"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
      hideBackdrop
    >
      {loading ? (
        <div className="text-center py-4">
          <Spinner color="primary" />
        </div>
      ) : !record ? (
        <div className="text-center py-4">
          <p className="text-muted mb-0">Kayıt bulunamadı</p>
        </div>
      ) : (
        <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
          {/* Header Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
            <div className="d-flex align-items-start justify-content-between">
              <div style={{ flex: 1 }}>
                <h4 className="mb-1" style={{ fontWeight: 600 }}>
                  {formatDate(record.recordDate)}
                </h4>
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                  {record.site.project.city.name} • {record.site.project.name} • {record.site.name}
                </p>
              </div>
              <span
                className={`px-2 py-1 rounded-pill small ${
                  record.isCompleted
                    ? 'bg-success bg-opacity-10 text-success'
                    : 'bg-warning bg-opacity-10 text-warning'
                }`}
                style={{ fontWeight: 500 }}
              >
                {record.isCompleted ? 'Kilitli' : 'Açık'}
              </span>
            </div>
          </div>

          {/* Weather & Creator Info Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
            <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              Bilgiler
            </div>
            <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
              {record.weatherCondition && (
                <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                  <span className="text-muted">Hava Durumu</span>
                  <span style={{ fontWeight: 500 }}>
                    <i className={`${weatherIcons[record.weatherCondition]} me-1`}></i>
                    {weatherLabels[record.weatherCondition]}
                    {record.temperature !== null && ` (${record.temperature}°C)`}
                  </span>
                </div>
              )}
              <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
                <span className="text-muted">Oluşturan</span>
                <span style={{ fontWeight: 500 }}>{record.createdBy.firstName} {record.createdBy.lastName}</span>
              </div>
              <div className="d-flex align-items-center justify-content-between px-3 py-2">
                <span className="text-muted">Kayıt Tarihi</span>
                <span>{new Date(record.createdAt).toLocaleString('tr-TR')}</span>
              </div>
            </div>
          </div>

          {/* Notes Card */}
          {record.notes && (
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
              <div className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                Notlar
              </div>
              <p className="mb-0" style={{ color: '#495057' }}>{record.notes}</p>
            </div>
          )}

          {/* Personnel Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                <i className="ri-team-line me-1 text-primary"></i>
                Personel
              </span>
              <span className="text-muted small">{record.personnelEntries.length} kişi</span>
            </div>
            {record.personnelEntries.length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>Personel kaydı yok</p>
            ) : (
              <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                {record.personnelEntries.map((entry, idx) => (
                  <div
                    key={entry.id}
                    className="d-flex align-items-center justify-content-between px-3 py-2"
                    style={{ borderBottom: idx < record.personnelEntries.length - 1 ? '1px solid #e9ecef' : 'none' }}
                  >
                    <div>
                      <span style={{ fontWeight: 500 }}>
                        {entry.personnel.firstName} {entry.personnel.lastName}
                      </span>
                      {entry.personnel.title && (
                        <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                          ({entry.personnel.title})
                        </span>
                      )}
                    </div>
                    <div className="d-flex gap-1">
                      <Badge color="info">{entry.dailyWage} yevmiye</Badge>
                      {entry.overtimeHours && (
                        <Badge color="warning">{entry.overtimeHours} sa mesai</Badge>
                      )}
                    </div>
                  </div>
                ))}
                <div className="d-flex align-items-center justify-content-between px-3 py-2 bg-white">
                  <span style={{ fontWeight: 500 }}>Toplam</span>
                  <div className="d-flex gap-2 align-items-center">
                    <span style={{ fontWeight: 600 }}>
                      {record.personnelEntries.reduce((sum, e) => sum + Number(e.dailyWage), 0)} yevmiye
                    </span>
                    {record.personnelEntries.reduce((sum, e) => sum + (e.overtimeHours || 0), 0) > 0 && (
                      <Badge color="warning" style={{ fontWeight: 600 }}>
                        {record.personnelEntries.reduce((sum, e) => sum + (e.overtimeHours || 0), 0)} sa mesai
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Vehicles Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                <i className="ri-truck-line me-1 text-success"></i>
                Araçlar
              </span>
              <span className="text-muted small">{record.vehicleEntries.length} araç</span>
            </div>
            {record.vehicleEntries.length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>Araç kaydı yok</p>
            ) : (
              <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                {record.vehicleEntries.map((entry, idx) => (
                  <div
                    key={entry.id}
                    className="px-3 py-2"
                    style={{ borderBottom: idx < record.vehicleEntries.length - 1 ? '1px solid #e9ecef' : 'none' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span style={{ fontWeight: 500 }}>{entry.vehicle.plate}</span>
                        <span className="text-muted ms-2" style={{ fontSize: '0.85rem' }}>
                          {entry.vehicle.brand} {entry.vehicle.model}
                        </span>
                      </div>
                      <span
                        className={`px-2 py-1 rounded-pill small ${
                          entry.usageType === 'FULL_DAY'
                            ? 'bg-primary bg-opacity-10 text-primary'
                            : 'bg-secondary bg-opacity-10 text-secondary'
                        }`}
                        style={{ fontWeight: 500, fontSize: '0.75rem' }}
                      >
                        {getUsageTypeLabel(entry.usageType)}
                      </span>
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: '0.85rem' }}>
                      <i className="ri-user-line me-1"></i>
                      Sürücü: {getDriverName(entry)}
                    </div>
                    {entry.km != null && (
                      <div className="mt-1" style={{ fontSize: '0.85rem' }}>
                        <i className="ri-route-line me-1"></i>
                        {entry.km} km
                      </div>
                    )}
                    {entry.hours != null && (
                      <div className="mt-1" style={{ fontSize: '0.85rem' }}>
                        <i className="ri-time-line me-1"></i>
                        {Number(entry.hours)} saat
                      </div>
                    )}
                    {entry.notes && (
                      <div className="text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                        {entry.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Work Entries Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                <i className="ri-tools-line me-1 text-warning"></i>
                Yapılan İşler
              </span>
              <span className="text-muted small">{record.workEntries.length} iş</span>
            </div>
            {record.workEntries.length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>İş kaydı yok</p>
            ) : (
              <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                {record.workEntries.map((entry, idx) => (
                  <div
                    key={entry.id}
                    className="d-flex align-items-center justify-content-between px-3 py-2"
                    style={{ borderBottom: idx < record.workEntries.length - 1 ? '1px solid #e9ecef' : 'none' }}
                  >
                    <div>
                      <span style={{ fontWeight: 500 }}>{entry.siteWork.workType.name}</span>
                      {entry.notes && (
                        <span className="text-muted d-block" style={{ fontSize: '0.8rem' }}>
                          {entry.notes}
                        </span>
                      )}
                    </div>
                    <Badge color="dark">
                      {Number(entry.quantity).toLocaleString('tr-TR')} {entry.siteWork.workType.unit}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Photos Card */}
          {record.photos.length > 0 && (
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                  <i className="ri-image-line me-1 text-info"></i>
                  Fotoğraflar
                </span>
                <span className="text-muted small">{record.photos.length} fotoğraf</span>
              </div>
              <div className="row g-2">
                {record.photos.map((photo) => (
                  <div key={photo.id} className="col-4">
                    <div className="position-relative">
                      <AuthImage
                        src={photo.filePath}
                        alt={photo.description || photo.fileName}
                        className="img-fluid rounded"
                        style={{ width: '100%', height: '100px', objectFit: 'cover', cursor: 'pointer' }}
                        onClick={() => openAuthFile(photo.filePath)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </SideModal>
  );
};

export default DailyRecordDetailModal;
